import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Calendar, Plus, ChevronLeft, ChevronRight, Settings, CalendarDays, 
  PartyPopper, Sun, Moon, Clock, Trash2, Edit, Save, FileText,
  GraduationCap, Users, Trophy, Sparkles, Bell, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const holidayTypes = [
  { value: 'government', label: 'Government Holiday', color: 'bg-red-500', icon: '🏛️' },
  { value: 'religious', label: 'Religious Holiday', color: 'bg-purple-500', icon: '🙏' },
  { value: 'school', label: 'School Holiday', color: 'bg-blue-500', icon: '🏫' },
  { value: 'vacation', label: 'Vacation', color: 'bg-green-500', icon: '🏖️' }
];

const eventTypes = [
  { value: 'exam', label: 'Exam', icon: '📝' },
  { value: 'ptm', label: 'Parent-Teacher Meeting', icon: '👨‍👩‍👧' },
  { value: 'sports', label: 'Sports Day', icon: '🏃' },
  { value: 'annual_function', label: 'Annual Function', icon: '🎭' },
  { value: 'cultural', label: 'Cultural Event', icon: '🎨' },
  { value: 'competition', label: 'Competition', icon: '🏆' },
  { value: 'other', label: 'Other', icon: '📅' }
];

const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const AcademicCalendarPage = () => {
  const { getAuthHeader, user } = useAuth();
  const [activeTab, setActiveTab] = useState('calendar');
  const [loading, setLoading] = useState(true);
  
  // Role check - Teacher can only view
  const isAdmin = user?.role === 'school_admin' || user?.role === 'super_admin';
  
  // Calendar state
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [monthData, setMonthData] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  
  // Data
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [workingDaySettings, setWorkingDaySettings] = useState(null);
  
  // Dialogs
  const [holidayDialog, setHolidayDialog] = useState(false);
  const [eventDialog, setEventDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingHoliday, setEditingHoliday] = useState(null);
  
  // Forms
  const [holidayForm, setHolidayForm] = useState({
    name: '',
    name_local: '',
    holiday_type: 'government',
    start_date: '',
    end_date: '',
    description: '',
    is_recurring: false,
    applies_to_staff: true,
    applies_to_students: true
  });
  
  const [eventForm, setEventForm] = useState({
    title: '',
    event_type: 'other',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    description: '',
    location: '',
    is_holiday: false,
    notify_parents: false
  });

  useEffect(() => {
    fetchData();
  }, [currentYear, currentMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [monthRes, dashboardRes, holidaysRes, eventsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/calendar/month-view/${currentYear}/${currentMonth}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/calendar/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/calendar/holidays?year=${currentYear}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/calendar/events?year=${currentYear}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/calendar/working-days/settings`, { headers: getAuthHeader() })
      ]);
      
      setMonthData(monthRes.data);
      setDashboard(dashboardRes.data);
      setHolidays(holidaysRes.data);
      setEvents(eventsRes.data);
      setWorkingDaySettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const openHolidayDialog = (date = null, holiday = null) => {
    if (holiday) {
      setEditingHoliday(holiday);
      setHolidayForm({
        name: holiday.name,
        name_local: holiday.name_local || '',
        holiday_type: holiday.holiday_type,
        start_date: holiday.start_date,
        end_date: holiday.end_date || '',
        description: holiday.description || '',
        is_recurring: holiday.is_recurring || false,
        applies_to_staff: holiday.applies_to_staff !== false,
        applies_to_students: holiday.applies_to_students !== false
      });
    } else {
      setEditingHoliday(null);
      setHolidayForm({
        name: '',
        name_local: '',
        holiday_type: 'government',
        start_date: date || '',
        end_date: '',
        description: '',
        is_recurring: false,
        applies_to_staff: true,
        applies_to_students: true
      });
    }
    setHolidayDialog(true);
  };

  const saveHoliday = async () => {
    if (!holidayForm.name || !holidayForm.start_date) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      if (editingHoliday) {
        await axios.put(
          `${API_URL}/api/calendar/holidays/${editingHoliday.id}`,
          holidayForm,
          { headers: getAuthHeader() }
        );
        toast.success('Holiday updated');
      } else {
        await axios.post(
          `${API_URL}/api/calendar/holidays`,
          holidayForm,
          { headers: getAuthHeader() }
        );
        toast.success('Holiday added');
      }
      setHolidayDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save holiday');
    }
  };

  const deleteHoliday = async (id) => {
    if (!confirm('Delete this holiday?')) return;
    try {
      await axios.delete(`${API_URL}/api/calendar/holidays/${id}`, { headers: getAuthHeader() });
      toast.success('Holiday deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const openEventDialog = (date = null) => {
    setEventForm({
      title: '',
      event_type: 'other',
      start_date: date || '',
      end_date: '',
      start_time: '',
      end_time: '',
      description: '',
      location: '',
      is_holiday: false,
      notify_parents: false
    });
    setEventDialog(true);
  };

  const saveEvent = async () => {
    if (!eventForm.title || !eventForm.start_date) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/calendar/events`,
        eventForm,
        { headers: getAuthHeader() }
      );
      toast.success('Event added');
      setEventDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const deleteEvent = async (id) => {
    if (!confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/api/calendar/events/${id}`, { headers: getAuthHeader() });
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const saveWorkingDaySettings = async () => {
    try {
      await axios.post(
        `${API_URL}/api/calendar/working-days/settings`,
        workingDaySettings,
        { headers: getAuthHeader() }
      );
      toast.success('Working day settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'holiday': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300';
      case 'off': return 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-300';
      case 'half_day': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-300';
      default: return 'bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 border-gray-200';
    }
  };

  const renderCalendarGrid = () => {
    if (!monthData) return null;
    
    const { days, first_day_weekday } = monthData;
    const weeks = [];
    let currentWeek = [];
    
    // Add empty cells for days before the first day
    for (let i = 0; i < first_day_weekday; i++) {
      currentWeek.push(null);
    }
    
    days.forEach((day, index) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    });
    
    // Fill remaining days in last week
    while (currentWeek.length > 0 && currentWeek.length < 7) {
      currentWeek.push(null);
    }
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    
    return (
      <div className="border rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-7 bg-muted">
          {dayLabels.map((day, i) => (
            <div key={day} className={`p-2 text-center text-sm font-medium ${i >= 5 ? 'text-red-500' : ''}`}>
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar Days */}
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 border-t">
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className={`min-h-[100px] p-1 border-r last:border-r-0 cursor-pointer transition-colors ${
                  day ? getStatusColor(day.status) : 'bg-gray-50 dark:bg-gray-900'
                } ${day?.is_today ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                onClick={() => day && setSelectedDate(day)}
              >
                {day && (
                  <>
                    <div className="flex justify-between items-start">
                      <span className={`text-sm font-medium ${day.is_today ? 'bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center' : ''}`}>
                        {day.day}
                      </span>
                      {day.status === 'half_day' && (
                        <Badge variant="outline" className="text-[10px] px-1">½</Badge>
                      )}
                    </div>
                    
                    {/* Holidays */}
                    {day.holidays.slice(0, 2).map((h, i) => (
                      <div key={i} className="mt-1 text-[10px] bg-red-500 text-white px-1 rounded truncate">
                        {h.name}
                      </div>
                    ))}
                    
                    {/* Events */}
                    {day.events.slice(0, 2).map((e, i) => (
                      <div key={i} className="mt-1 text-[10px] bg-blue-500 text-white px-1 rounded truncate">
                        {e.title}
                      </div>
                    ))}
                    
                    {(day.holidays.length + day.events.length) > 2 && (
                      <div className="text-[10px] text-muted-foreground mt-1">
                        +{day.holidays.length + day.events.length - 2} more
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <DashboardLayout title="Academic Calendar">
      <div className="space-y-6">
        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${dashboard.today.is_working ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                    {dashboard.today.is_working ? <Sun className="h-5 w-5 text-green-600" /> : <Moon className="h-5 w-5 text-red-600" />}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Today</p>
                    <p className="font-semibold">{dashboard.today.type === 'holiday' ? 'Holiday' : dashboard.today.type === 'half_day' ? 'Half Day' : dashboard.today.type === 'off' ? 'Off' : 'Working'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                    <CalendarDays className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">This Month</p>
                    <p className="font-semibold">{dashboard.current_month.working_days} Working Days</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-lg">
                    <PartyPopper className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Holidays</p>
                    <p className="font-semibold">{dashboard.current_month.holidays} This Month</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Events</p>
                    <p className="font-semibold">{dashboard.year_stats.total_events} This Year</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle>Academic Calendar {currentYear}</CardTitle>
                <CardDescription>{isAdmin ? 'Manage holidays, events, and working days' : 'View school calendar, holidays and events'}</CardDescription>
              </div>
              {isAdmin && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => openHolidayDialog()}>
                  <Plus className="h-4 w-4 mr-2" /> Add Holiday
                </Button>
                <Button variant="outline" onClick={() => openEventDialog()}>
                  <Plus className="h-4 w-4 mr-2" /> Add Event
                </Button>
              </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="calendar" className="gap-2">
                  <Calendar className="h-4 w-4" /> Calendar
                </TabsTrigger>
                <TabsTrigger value="holidays" className="gap-2">
                  <PartyPopper className="h-4 w-4" /> Holidays
                </TabsTrigger>
                <TabsTrigger value="events" className="gap-2">
                  <Sparkles className="h-4 w-4" /> Events
                </TabsTrigger>
                {isAdmin && (
                <TabsTrigger value="settings" className="gap-2">
                  <Settings className="h-4 w-4" /> Settings
                </TabsTrigger>
                )}
              </TabsList>

              {/* Calendar View */}
              <TabsContent value="calendar">
                <div className="space-y-4">
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between">
                    <Button variant="outline" size="sm" onClick={handlePrevMonth}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <h3 className="text-lg font-semibold">
                      {monthData?.month_name} {currentYear}
                    </h3>
                    <Button variant="outline" size="sm" onClick={handleNextMonth}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Legend */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-white border rounded"></div>
                      <span>Working Day</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-amber-100 border border-amber-300 rounded"></div>
                      <span>Half Day</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
                      <span>Holiday</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
                      <span>Weekly Off</span>
                    </div>
                  </div>
                  
                  {/* Calendar Grid */}
                  {renderCalendarGrid()}
                  
                  {/* Month Summary */}
                  {monthData && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 p-4 bg-muted rounded-lg">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{monthData.total_days}</p>
                        <p className="text-sm text-muted-foreground">Total Days</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600">{monthData.working_days}</p>
                        <p className="text-sm text-muted-foreground">Working Days</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-600">{monthData.holidays_count}</p>
                        <p className="text-sm text-muted-foreground">Holidays</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-amber-600">{monthData.half_days_count}</p>
                        <p className="text-sm text-muted-foreground">Half Days</p>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Holidays List */}
              <TabsContent value="holidays">
                <div className="space-y-4">
                  <div className="flex gap-2 flex-wrap">
                    {holidayTypes.map(type => (
                      <Badge key={type.value} variant="outline" className="gap-1">
                        <span>{type.icon}</span> {type.label}
                      </Badge>
                    ))}
                  </div>
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Holiday</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Applies To</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {holidays.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            No holidays added yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        holidays.map(holiday => {
                          const typeInfo = holidayTypes.find(t => t.value === holiday.holiday_type);
                          return (
                            <TableRow key={holiday.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{holiday.name}</p>
                                  {holiday.name_local && (
                                    <p className="text-sm text-muted-foreground">{holiday.name_local}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={`${typeInfo?.color} text-white`}>
                                  {typeInfo?.icon} {typeInfo?.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {holiday.start_date}
                                {holiday.end_date && holiday.end_date !== holiday.start_date && (
                                  <span className="text-muted-foreground"> to {holiday.end_date}</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {holiday.applies_to_students && <Badge variant="outline">Students</Badge>}
                                  {holiday.applies_to_staff && <Badge variant="outline">Staff</Badge>}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => openHolidayDialog(null, holiday)}>
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => deleteHoliday(holiday.id)}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* Events List */}
              <TabsContent value="events">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No events added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      events.map(event => {
                        const typeInfo = eventTypes.find(t => t.value === event.event_type);
                        return (
                          <TableRow key={event.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{event.title}</p>
                                {event.description && (
                                  <p className="text-sm text-muted-foreground truncate max-w-xs">{event.description}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {typeInfo?.icon} {typeInfo?.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p>{event.start_date}</p>
                                {event.start_time && (
                                  <p className="text-sm text-muted-foreground">{event.start_time} - {event.end_time}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{event.location || '-'}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" onClick={() => deleteEvent(event.id)}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TabsContent>

              {/* Working Day Settings */}
              <TabsContent value="settings">
                {workingDaySettings && (
                  <div className="space-y-6">
                    {/* Weekly Schedule */}
                    <div>
                      <h3 className="font-semibold mb-4">Weekly Schedule</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {dayNames.map((day, index) => (
                          <div key={day} className="space-y-2">
                            <Label className="capitalize">{dayLabels[index]} ({day})</Label>
                            <Select
                              value={workingDaySettings[day] || 'off'}
                              onValueChange={(value) => setWorkingDaySettings({
                                ...workingDaySettings,
                                [day]: value
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="full_day">Full Day</SelectItem>
                                <SelectItem value="half_day">Half Day</SelectItem>
                                <SelectItem value="off">Off</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* School Timings */}
                    <div>
                      <h3 className="font-semibold mb-4">School Timings</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>School Start Time</Label>
                          <Input
                            type="time"
                            value={workingDaySettings.school_start_time || '10:00'}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              school_start_time: e.target.value
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>School End Time</Label>
                          <Input
                            type="time"
                            value={workingDaySettings.school_end_time || '16:00'}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              school_end_time: e.target.value
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Half Day End Time</Label>
                          <Input
                            type="time"
                            value={workingDaySettings.half_day_end_time || '13:00'}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              half_day_end_time: e.target.value
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Late Threshold (minutes)</Label>
                          <Input
                            type="number"
                            value={workingDaySettings.late_threshold_minutes || 15}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              late_threshold_minutes: parseInt(e.target.value)
                            })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Staff Timings */}
                    <div>
                      <h3 className="font-semibold mb-4">Staff Timings</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Staff Start Time</Label>
                          <Input
                            type="time"
                            value={workingDaySettings.staff_start_time || '09:30'}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              staff_start_time: e.target.value
                            })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Staff End Time</Label>
                          <Input
                            type="time"
                            value={workingDaySettings.staff_end_time || '16:30'}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              staff_end_time: e.target.value
                            })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Salary & Attendance Deduction Settings */}
                    <div className="border-t pt-6 mt-6">
                      <h3 className="font-semibold mb-4 text-lg">💰 Salary & Attendance Deduction Settings</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Late Deduction (₹/day)</Label>
                          <Input
                            type="number"
                            value={workingDaySettings.late_deduction_per_day || 50}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              late_deduction_per_day: parseFloat(e.target.value)
                            })}
                          />
                          <p className="text-xs text-muted-foreground">Amount deducted per late day</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Max Late Days Allowed</Label>
                          <Input
                            type="number"
                            value={workingDaySettings.max_late_days_allowed || 3}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              max_late_days_allowed: parseInt(e.target.value)
                            })}
                          />
                          <p className="text-xs text-muted-foreground">Free late days before deduction</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Late Count for Half Day</Label>
                          <Input
                            type="number"
                            value={workingDaySettings.late_count_for_half_day || 3}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              late_count_for_half_day: parseInt(e.target.value)
                            })}
                          />
                          <p className="text-xs text-muted-foreground">X late = 1 half day absent</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Overtime Rate (₹/hour)</Label>
                          <Input
                            type="number"
                            value={workingDaySettings.overtime_rate_per_hour || 100}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              overtime_rate_per_hour: parseFloat(e.target.value)
                            })}
                          />
                          <p className="text-xs text-muted-foreground">OT payment per hour</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Half Day Deduction (%)</Label>
                          <Input
                            type="number"
                            value={workingDaySettings.half_day_salary_deduction || 50}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              half_day_salary_deduction: parseFloat(e.target.value)
                            })}
                          />
                          <p className="text-xs text-muted-foreground">% of day salary for half day absent</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Working Days/Month</Label>
                          <div className="p-2 bg-muted rounded border text-lg font-semibold">
                            {(() => {
                              const dayValues = { full_day: 1, half_day: 0.5, off: 0 };
                              const weekly = 
                                dayValues[workingDaySettings.monday || 'full_day'] +
                                dayValues[workingDaySettings.tuesday || 'full_day'] +
                                dayValues[workingDaySettings.wednesday || 'full_day'] +
                                dayValues[workingDaySettings.thursday || 'full_day'] +
                                dayValues[workingDaySettings.friday || 'full_day'] +
                                dayValues[workingDaySettings.saturday || 'off'] +
                                dayValues[workingDaySettings.sunday || 'off'];
                              return Math.round(weekly * 4.33);
                            })()}
                          </div>
                          <p className="text-xs text-muted-foreground">Auto-calculated from weekly schedule</p>
                        </div>
                        <div className="space-y-2 col-span-2 flex items-center gap-3 pt-6">
                          <input
                            type="checkbox"
                            id="unpaid_leave_deduction"
                            checked={workingDaySettings.unpaid_leave_deduction !== false}
                            onChange={(e) => setWorkingDaySettings({
                              ...workingDaySettings,
                              unpaid_leave_deduction: e.target.checked
                            })}
                            className="h-4 w-4"
                          />
                          <Label htmlFor="unpaid_leave_deduction">Deduct salary for unpaid leaves</Label>
                        </div>
                      </div>
                    </div>

                    <Button onClick={saveWorkingDaySettings}>
                      <Save className="h-4 w-4 mr-2" /> Save Settings
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Upcoming Section */}
        {dashboard && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Upcoming Holidays */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PartyPopper className="h-5 w-5 text-red-500" />
                  Upcoming Holidays
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard.upcoming_holidays.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No upcoming holidays</p>
                ) : (
                  <div className="space-y-3">
                    {dashboard.upcoming_holidays.map(h => (
                      <div key={h.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium">{h.name}</p>
                          {h.name_local && <p className="text-sm text-muted-foreground">{h.name_local}</p>}
                        </div>
                        <Badge variant="outline">{h.start_date}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  Upcoming Events
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard.upcoming_events.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No upcoming events</p>
                ) : (
                  <div className="space-y-3">
                    {dashboard.upcoming_events.map(e => {
                      const typeInfo = eventTypes.find(t => t.value === e.event_type);
                      return (
                        <div key={e.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">{typeInfo?.icon} {e.title}</p>
                            {e.location && <p className="text-sm text-muted-foreground">{e.location}</p>}
                          </div>
                          <Badge variant="outline">{e.start_date}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Add Holiday Dialog */}
      <Dialog open={holidayDialog} onOpenChange={setHolidayDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Holiday Name *</Label>
                <Input
                  placeholder="Republic Day"
                  value={holidayForm.name}
                  onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Local Name (Bengali)</Label>
                <Input
                  placeholder="Republic Day"
                  value={holidayForm.name_local}
                  onChange={(e) => setHolidayForm({ ...holidayForm, name_local: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Holiday Type</Label>
              <Select
                value={holidayForm.holiday_type}
                onValueChange={(value) => setHolidayForm({ ...holidayForm, holiday_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {holidayTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={holidayForm.start_date}
                  onChange={(e) => setHolidayForm({ ...holidayForm, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date (for vacations)</Label>
                <Input
                  type="date"
                  value={holidayForm.end_date}
                  onChange={(e) => setHolidayForm({ ...holidayForm, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Optional description"
                value={holidayForm.description}
                onChange={(e) => setHolidayForm({ ...holidayForm, description: e.target.value })}
              />
            </div>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={holidayForm.applies_to_students}
                  onCheckedChange={(checked) => setHolidayForm({ ...holidayForm, applies_to_students: checked })}
                />
                <Label>Students</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={holidayForm.applies_to_staff}
                  onCheckedChange={(checked) => setHolidayForm({ ...holidayForm, applies_to_staff: checked })}
                />
                <Label>Staff</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={holidayForm.is_recurring}
                  onCheckedChange={(checked) => setHolidayForm({ ...holidayForm, is_recurring: checked })}
                />
                <Label>Recurring</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolidayDialog(false)}>Cancel</Button>
            <Button onClick={saveHoliday}>
              {editingHoliday ? 'Update' : 'Add'} Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog open={eventDialog} onOpenChange={setEventDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Event Title *</Label>
              <Input
                placeholder="Annual Sports Day"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select
                value={eventForm.event_type}
                onValueChange={(value) => setEventForm({ ...eventForm, event_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={eventForm.start_time}
                  onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={eventForm.end_time}
                  onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                placeholder="School Ground"
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Event details..."
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
              />
            </div>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={eventForm.is_holiday}
                  onCheckedChange={(checked) => setEventForm({ ...eventForm, is_holiday: checked })}
                />
                <Label>Mark as Holiday</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={eventForm.notify_parents}
                  onCheckedChange={(checked) => setEventForm({ ...eventForm, notify_parents: checked })}
                />
                <Label>Notify Parents</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEventDialog(false)}>Cancel</Button>
            <Button onClick={saveEvent}>Add Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AcademicCalendarPage;
